import * as Sentry from '@sentry/react';
import { scrubEventData } from './sentry-scrub.js';
import type { ErrorEvent } from '@sentry/react';

/**
 * Browser error reports to Sentry (docs/adr/0020-safety.md). Off unless the build has
 * VITE_SENTRY_DSN. Reports carry the error, the stack, the page path and the workspace and user ids,
 * never what people typed or read: no breadcrumbs, no replay, no request or query data.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

export function initSentry(): void {
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_CHAPSI_ENV as string | undefined) ?? import.meta.env.MODE,
    ...(import.meta.env.VITE_APP_RELEASE ? { release: import.meta.env.VITE_APP_RELEASE as string } : {}),
    dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false },
    tracesSampleRate: 0,
    maxBreadcrumbs: 0,
    beforeSend: scrub,
  });
}

/** Tags reports with who was signed in (ids only). */
export function setReportIdentity(userId: string | null, tenantId: string | null): void {
  if (!dsn) return;
  Sentry.setUser(userId ? { id: userId } : null);
  Sentry.setTag('tenant_id', tenantId ?? undefined);
}

export function reportError(err: unknown): void {
  if (dsn) Sentry.captureException(err);
}

/** Keeps the error, stack, page and ids; drops request data, breadcrumbs and extras, and every token (sentry-scrub.ts). */
function scrub(event: ErrorEvent): ErrorEvent | null {
  scrubEventData(event, ['os', 'browser', 'device', 'app', 'trace']);
  return event;
}
